/**
 * Document Upload Types
 * TypeScript interfaces for document upload functionality
 * Aligned with Firebase Functions trigger-based architecture
 */

import { Timestamp } from 'firebase/firestore';

/**
 * Supported file types for document upload
 */
export const SUPPORTED_FILE_TYPES = {
  'application/pdf': '.pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
  'text/plain': '.txt',
  'text/markdown': '.md',
  'text/x-markdown': '.md',
} as const;

export type SupportedMimeType = keyof typeof SUPPORTED_FILE_TYPES;

/**
 * Upload state machine - matches backend workflow
 */
export type UploadState = 
  | 'idle'          // Ready to start upload
  | 'validating'    // Validating files
  | 'uploading'     // Uploading to Firebase Storage
  | 'processing'    // Backend processing
  | 'done'          // Upload complete
  | 'error';        // Error occurred

/**
 * Individual file upload status
 */
export interface FileUploadStatus {
  id: string;                    // Unique file ID
  file: File;                    // Original file object
  name: string;                  // File name
  size: number;                  // File size in bytes
  mimeType: string;              // MIME type
  status: UploadState;           // Current upload state
  progress: number;              // Upload progress (0-100)
  error?: string;                // Error message if failed
  storagePath?: string;          // Firebase Storage path after upload
}

/**
 * Base document metadata structure
 * Path: /users/{userId}/documents/{documentId}
 */
export interface BaseDocumentMetadata {
  id: string;                    // documentId (UUID)
  userId: string;                // User who uploaded
  conversationId: string;        // Associated conversation
  messageId: string;             // Associated message (pre-generated)
  fileName: string;              // Original file name
  fileSize: number;              // File size in bytes
  mimeType: string;              // MIME type
  storagePath: string;           // Firebase Storage path
  isMessageSent: boolean;        // Whether document was sent in a message
  isDocumentDeleted: boolean;    // Whether document was logically deleted
  createdAt: Timestamp;          // Upload timestamp
  updatedAt?: Timestamp;         // Last update timestamp
  deletedAt?: Timestamp;         // Deletion timestamp (for audit trail)
  metadata?: {                   // Additional metadata
    browser?: string;            // navigator.userAgent
    uploadDuration?: number;     // Upload time in ms
  };
}

/**
 * Document metadata for frontend creation
 */
export interface DocumentMetadata extends BaseDocumentMetadata {
  status: 'processing';          // Initial status
}

/**
 * Processed document metadata (backend updates)
 */
export interface ProcessedDocumentMetadata extends BaseDocumentMetadata {
  status: 'processing' | 'completed' | 'error';  // All possible statuses (backend uses 'completed')
  processedAt?: Timestamp;                       // Processing completion time
  error?: string;                               // Error details
  processingDuration?: number;                  // Processing time in ms
  extractedText?: string;                       // Extracted text content
  pageCount?: number;                           // Number of pages (for PDFs)
  
  // Summary processing fields
  summaryStatus?: 'processing' | 'completed';  // Summary processing status
  summarySkipped?: boolean;                    // Whether summary was skipped (e.g., document too short)
  summaryError?: string;                       // Error message if summary failed
  summary?: string;                            // Generated summary text
  summaryGeneratedAt?: Timestamp;              // When summary was generated
  summarizationDuration?: number;              // Summary generation time in ms
  summarySkipReason?: string;                  // Reason why summary was skipped
  
  // Additional backend fields
  textLength?: number;                         // Length of extracted text
  sourceWordCount?: number;                    // Word count of source document
  chunkCount?: number;                         // Number of chunks created
  embeddingCount?: number;                     // Number of embeddings created
  executionId?: string;                        // Backend execution/conversation ID
}

/**
 * Attachment info stored in Message
 */
export interface MessageAttachment {
  id: string;                    // Document ID
  type: 'document' | 'url';      // Attachment type
  name: string;                  // File name or URL
  size?: number;                 // File size (documents only)
  mimeType?: string;             // MIME type (documents only)
  url?: string;                  // URL (for URL attachments)
  storagePath?: string;          // Firebase Storage path (documents only)
  status: 'uploading' | 'done' | 'error';  // Upload status
  error?: string;                // Error message if failed
  metadata?: {                   // URL metadata
    title?: string;              // Page title
    description?: string;        // Page description
    image?: string;              // Preview image URL
  };
}

/**
 * Document upload configuration
 */
export const UPLOAD_CONFIG = {
  MAX_FILE_SIZE_MB: 19,          // Maximum file size per file
  MAX_FILES_PER_BATCH: 10,       // Maximum files per upload batch
  CHUNK_SIZE: 512 * 1024,        // Upload chunk size (512KB)
  STORAGE_PATH_PREFIX: 'document-uploads',  // Storage path prefix
  
  // Validation error keys (for translation)
  VALIDATION_ERRORS: {
    FILE_TOO_LARGE: 'FILE_TOO_LARGE',
    UNSUPPORTED_TYPE: 'UNSUPPORTED_TYPE',
    TOO_MANY_FILES: 'TOO_MANY_FILES',
    FILE_EMPTY: 'FILE_EMPTY',
    UPLOAD_FAILED: 'UPLOAD_FAILED',
    PROCESSING_FAILED: 'PROCESSING_FAILED',
  }
} as const;

/**
 * Document upload hook return interface
 */
export interface UseDocumentUploadReturn {
  // State
  uploadState: UploadState;
  fileStatuses: FileUploadStatus[];
  isUploading: boolean;
  totalProgress: number;          // Overall progress (0-100)
  
  // Actions
  selectFiles: (files: FileList | File[]) => void;
  removeFile: (fileId: string) => void;
  startUpload: () => Promise<void>;
  cancelUpload: () => void;
  clearFiles: () => void;
  
  // Validation
  validateFiles: (files: FileList | File[]) => { valid: File[]; invalid: { file: File; error: string }[] };
}

/**
 * URL metadata for URL attachments
 */
export interface URLMetadata {
  url: string;
  title?: string;
  description?: string;
  image?: string;
  favicon?: string;
  siteName?: string;
}

/**
 * Helper function to check if a string is a valid URL
 */
export const isValidURL = (text: string): boolean => {
  try {
    const url = new URL(text);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
};

/**
 * Helper function to extract URLs from text
 */
export const extractURLsFromText = (text: string): string[] => {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const matches = text.match(urlRegex) || [];
  return matches.filter(isValidURL);
};

/**
 * Helper function to check if a document is fully processed
 * A document is considered fully processed when:
 * - status is 'completed' AND
 * - summaryStatus is 'completed'
 * 
 * This ensures both document processing and summary generation are complete
 * (or summary was intentionally skipped) before marking as done.
 */
export const isDocumentFullyProcessed = (doc: ProcessedDocumentMetadata): boolean => {
  return doc.status === 'completed' && doc.summaryStatus === 'completed';
};
